import { SolrParamsKey } from '#shared/solr'
import { sanitizeFootnoteDomId } from '#shared/utils/html/footnotes'
import { useFootnoteOpenState } from '~/composables/footnotes/useFootnotesOpenSate'
import { solrBookFootnoteResponseSchema } from '~/utils/solr/docs-response'

/**
 * Composable for managing literature footnotes. Fetches content from Solr whenever a footnote is opened, and provides methods to open and close footnotes.
 *
 * @param bookId - The ID of the book to which the footnotes belong.
 */
export function useLiteratureFootnotes(bookId: MaybeRefOrGetter<string>) {
  const { openState, open, close } = useFootnoteOpenState()

  // Resolved in setup, because the computed below reruns outside it when a footnote opens
  const fetch = useRequestFetch()
  const { user } = useUserSession()

  const queries = useQueries({
    queries: computed(() => [...openState.keys()].sort().map(
      id => buildSolrQueryOptions({
        endpoint: 'footnotes',
        params: {
          [SolrParamsKey.Id]: `${toValue(bookId)}/footnotes/${id}`,
          [SolrParamsKey.ContentType]: ['book_footnote'],
        },
        schema: solrBookFootnoteResponseSchema,
      }, { fetch, user }),
    )),
  })

  const { $parseStringToDocument } = useNuxtApp()

  return {
    open,
    close,
    data: computed(
      () => queries.value.reduce<FootnotesState>((acc, q) => {
        const footnote = q.data?.response?.docs?.[0]
        const document = $parseStringToDocument(
          footnote?.bodytext,
          // Order is significant: create-footnote-content must run before process-links
          // so it can read the original .fnr anchor as the footnote label.
          ['clean-corrupted-attributes', 'strip-epub-resources', 'create-footnote-content', 'process-links'],
          { source: 'epub' },
        )

        const content = document.body.firstChild as HTMLElement | null
        const id = sanitizeFootnoteDomId(footnote?.footnoteId) ?? sanitizeFootnoteDomId(content?.id)

        if (content && id) {
          content.id = id

          const closeButton = content.querySelector<HTMLElement>('[data-footnote-id]')
          if (closeButton?.dataset) {
            closeButton.dataset.footnoteId = id
          }

          acc[id] = {
            content: document,
            open: openState.get(id) ?? false,
          }
        }

        return acc
      }, {}),
    ),
  }
}
